import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { getLabels } from '../../../api';

export interface LabelMeta {
    id: string;
    name: string;
    color?: string;
}

export type LabelsById = Record<string, LabelMeta>;

export const labelKeys = {
    all: ['labels'] as const,
};

const NO_LABELS: LabelsById = {};

// The labels API has used several field spellings over time; normalise them once.
export const normalizeLabel = (label: any): LabelMeta | null => {
    const id = label?.id || label?.label_id || label?.labelId;
    if (!id) return null;
    return {
        id,
        name: label?.name || label?.label_name || label?.labelName || label?.label || id,
        color: label?.color || label?.label_color || label?.labelColor,
    };
};

export const toLabelsById = (res: any): LabelsById => {
    const map: LabelsById = {};
    (res?.labels || []).forEach((label: any) => {
        const normalized = normalizeLabel(label);
        if (normalized) map[normalized.id] = normalized;
    });
    return map;
};

/** The company's labels, by id for chips and as a list (in API order) for pickers. */
export const useLabelsQuery = ({ enabled = true }: { enabled?: boolean } = {}) => {
    const { data, isPending } = useQuery({
        queryKey: labelKeys.all,
        queryFn: async () => {
            const res = await getLabels();
            if (res?.status === 'fail') throw new Error(res.message ?? 'getLabels failed');
            return toLabelsById(res);
        },
        enabled,
    });
    const labelsById = data ?? NO_LABELS;
    const labels = useMemo(() => Object.values(labelsById), [labelsById]);
    return { labelsById, labels, isPending };
};
